import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import SEO from '../components/SEO';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'How do you choose which casinos to review?',
      answer: 'We only review casinos licensed by the UK Gambling Commission. We prioritize casinos with strong reputations, excellent player feedback, competitive bonuses, and reliable payment processing. Every casino we feature undergoes thorough testing before we publish our review.',
    },
    {
      question: 'Are your reviews unbiased?',
      answer: 'Absolutely. While we may receive affiliate commissions when players sign up through our links, this never influences our ratings or recommendations. We test every casino independently, and our reviews reflect genuine player experiences. We\'ve given low ratings to casinos that pay us commissions and high ratings to those that don\'t.',
    },
    {
      question: 'What makes a casino get a high rating?',
      answer: 'Our rating system evaluates multiple factors: game selection and quality (25%), bonus terms and value (20%), withdrawal speed and reliability (20%), customer support quality (15%), mobile experience (10%), and security/licensing (10%). Casinos need to excel across all areas to earn top ratings.',
    },
    {
      question: 'How quickly can I withdraw my winnings?',
      answer: 'Withdrawal times vary by casino and payment method. E-wallets (PayPal, Skrill, Neteller) typically process within 0-24 hours. Bank transfers can take 3-5 business days. Cryptocurrency withdrawals are often instant. We test and document withdrawal speeds for each casino in our reviews.',
    },
    {
      question: 'What are wagering requirements?',
      answer: 'Wagering requirements (also called playthrough requirements) determine how many times you must bet your bonus amount before you can withdraw winnings. For example, a £100 bonus with 35x wagering means you must bet £3,500 before withdrawing. Lower wagering requirements are better for players.',
    },
    {
      question: 'Can I claim multiple welcome bonuses?',
      answer: 'Generally, no. Welcome bonuses are typically limited to one per person, household, or IP address. However, you can claim welcome bonuses at different casinos. Some casinos also offer reload bonuses, cashback, and other promotions for existing players.',
    },
    {
      question: 'Are online casinos safe?',
      answer: 'UK-licensed casinos are highly regulated and safe. They must use SSL encryption, keep player funds in segregated accounts, undergo regular audits, and provide responsible gambling tools. Always verify a casino\'s UKGC license number before playing. We only recommend fully licensed operators.',
    },
    {
      question: 'What payment methods are best for UK players?',
      answer: 'Popular options include debit cards (Visa, Mastercard), e-wallets (PayPal, Skrill, Neteller), bank transfers, and some casinos accept cryptocurrency. E-wallets usually offer the fastest withdrawals. We test and document payment options for each casino in our reviews.',
    },
    {
      question: 'How do I know if a bonus is worth claiming?',
      answer: 'Consider the bonus amount, wagering requirements, maximum bet limits, game restrictions, and time limits. A £200 bonus with 40x wagering might be less valuable than a £100 bonus with 25x wagering. We break down the true value of each bonus in our reviews.',
    },
    {
      question: 'What should I do if I have a problem with a casino?',
      answer: 'First, contact the casino\'s customer support directly. If that doesn\'t resolve the issue, you can file a complaint with the UK Gambling Commission or use the casino\'s Alternative Dispute Resolution (ADR) service. We document each casino\'s complaint resolution process in our reviews.',
    },
    {
      question: 'Do you update your reviews?',
      answer: 'Yes, we regularly update our reviews to reflect changes in bonuses, terms, game selections, and player feedback. Major updates are noted at the top of each review with the date. We also monitor casinos for any significant changes that might affect our ratings.',
    },
    {
      question: 'How can I gamble responsibly?',
      answer: 'Set deposit limits, use time limits, never chase losses, and only gamble with money you can afford to lose. All UK-licensed casinos must provide responsible gambling tools like self-exclusion, reality checks, and links to support organizations like BeGambleAware and GamCare.',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <SEO
        title="Frequently Asked Questions - UK Casino Reviews"
        description="Get answers to common questions about UK online casinos, bonuses, withdrawals, wagering requirements, and more. Expert guidance from JackpotQuantum.com."
        canonical="/faq"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-dark-900">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-dark-800 to-dark-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                <span className="text-gradient">Frequently Asked</span> <span className="text-white">Questions</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed">
                Find answers to common questions about UK online casinos, bonuses, payments, and more. 
                Can't find what you're looking for? Contact us and we'll help.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-dark-800">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="glass rounded-xl overflow-hidden">
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-dark-700 transition-colors"
                    >
                      <span className="text-white font-semibold text-lg pr-4">{faq.question}</span>
                      {openIndex === index ? (
                        <FaChevronUp className="w-5 h-5 text-quantum-400 flex-shrink-0" />
                      ) : (
                        <FaChevronDown className="w-5 h-5 text-quantum-400 flex-shrink-0" />
                      )}
                    </button>
                    {openIndex === index && (
                      <div className="px-6 py-4 border-t border-dark-700">
                        <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 bg-dark-900">
          <div className="container mx-auto px-4 text-center">
            <div className="glass rounded-2xl p-12 max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-white mb-4">
                Still Have Questions?
              </h2>
              <p className="text-gray-300 mb-8">
                We're here to help. If you can't find the answer you're looking for, 
                feel free to reach out and we'll get back to you as soon as possible.
              </p>
              <button className="bg-gradient-to-r from-quantum-500 to-quantum-600 text-white py-4 px-8 rounded-xl font-bold text-lg hover:from-quantum-600 hover:to-quantum-700 transition-all transform hover:scale-105 shadow-lg shadow-quantum-500/30">
                Contact Us
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQ;

